import { useId, useState } from 'react'
import Icon from '../docs/Icon'
import Dialog from '../ui/Dialog'
import Flag from '../marks/Flag'
import { findMission, america, type Mission } from '@/content/missions'
import { usePrefs } from '@/state/prefs'
import { BANNER } from '@/content/identity'
import { OPERATOR } from '@/content/provenance'
import Cased from '@/components/ui/Cased'

/** The official notice and its explanation, as USWDS and America.gov word it. */
export function OfficialNotice() {
  return (
    <div className="mds-banner-notice">
      <div className="mds-banner-notice__item">
        <span className="mds-banner-notice__icon" aria-hidden="true">
          <Icon name="landmark" />
        </span>
        <div>
          <h3><Cased>Official websites use .gov</Cased></h3>
          <p>
            A <strong>.gov</strong> website belongs to an official government organization in the United States.
          </p>
        </div>
      </div>
      <div className="mds-banner-notice__item">
        <span className="mds-banner-notice__icon" aria-hidden="true">
          <Icon name="lock" />
        </span>
        <div>
          <h3><Cased>Secure .gov websites use HTTPS</Cased></h3>
          <p>
            A <strong>lock</strong> or <strong>https://</strong> means you have safely connected to the .gov website.
            Share sensitive information only on official, secure websites.
          </p>
        </div>
      </div>
    </div>
  )
}

type BannerProps = {
  /** Show the official wording; defaults to whether a mission is selected. */
  official?: boolean
  mission?: Mission
  /** `dialog` explains in a modal; `expand` opens "Here's how you know" inline. */
  mode?: 'dialog' | 'expand'
  align?: 'center' | 'left'
  /** Where the alternate wording's dialog links for guidance. */
  guidanceHref?: string
  /** Called when that link is followed, so a router can take over. */
  onNavigate?: (href: string) => void
  /** A copy shown as an example, which is not a page landmark. */
  specimen?: boolean
}

/** The explanation for the alternate wording: who built the site and how it is served. */
export function SiteNotice({ guidanceHref, onNavigate }: { guidanceHref: string; onNavigate?: () => void }) {
  return (
    <div className="mds-banner-notice">
      <div className="mds-banner-notice__item">
        <span className="mds-banner-notice__icon" aria-hidden="true">
          <Icon name="globe" />
        </span>
        <div>
          <h3>
            Built by <a href={OPERATOR.url}>{OPERATOR.name}</a>
          </h3>
          <p>
            Mission Design System is the design system for United States government websites. Agencies that adopt it
            show the official notice on their own .gov sites.{' '}
            <a
              className="mds-link"
              href={guidanceHref}
              onClick={(event) => {
                if (onNavigate) {
                  event.preventDefault()
                  onNavigate()
                }
              }}
            >
              Banner guidance
            </a>
          </p>
        </div>
      </div>
      <div className="mds-banner-notice__item">
        <span className="mds-banner-notice__icon" aria-hidden="true">
          <Icon name="lock" />
        </span>
        <div>
          <h3>
            Secure <code>https://</code> connection
          </h3>
          <p>
            A <strong>lock</strong> or <strong>https://</strong> means you are connected securely. Every page of this
            site is served over HTTPS.
          </p>
        </div>
      </div>
    </div>
  )
}

/**
 * The line above the header that says whose site this is. It explains itself
 * either in a dialog behind an information button or in a "Here's how you
 * know" panel that expands in place, and sits centered or left-aligned.
 */
export default function Banner({
  official,
  mission,
  mode,
  align,
  guidanceHref = '/foundations/identity',
  onNavigate,
  specimen = false,
}: BannerProps) {
  const { prefs } = usePrefs()
  const [open, setOpen] = useState(false)
  const panelId = useId()
  const active = mission ?? findMission(prefs.mission) ?? america
  const isOfficial = official ?? active.id !== america.id
  // The official line shown because a visitor chose an agency, not because
  // this site is that agency's: it carries a Preview tag.
  const preview = official === undefined && isOfficial
  const previewTag = preview && (
    <span className="mds-banner__preview" title={`Preview: not an official ${active.short} site`}>
      Preview
    </span>
  )
  // What a preview is, and where the disclaimer explains it.
  const disclaimerHref = '/disclaimer#previews'
  const previewNote = (
    <p className="mds-hint">
      You are previewing {active.name} on Mission Design System, which is not affiliated with or endorsed by{' '}
      {active.short}. A site that adopts MDS shows this notice on its own .gov domain.{' '}
      <a
        href={disclaimerHref}
        onClick={(event) => {
          if (!onNavigate) return
          event.preventDefault()
          setOpen(false)
          onNavigate(disclaimerHref)
        }}
      >
        Read the disclaimer
      </a>
    </p>
  )
  const text = isOfficial ? BANNER.official : BANNER.site
  const how = mode ?? prefs.bannerMode
  const side = align ?? prefs.bannerAlign
  const flag = <Flag variant={prefs.mark === 'motion' ? 'flag' : prefs.mark} className="mds-banner__flag" />
  const explanation = isOfficial ? (
    <>
      <OfficialNotice />
      {preview && previewNote}
    </>
  ) : (
    <SiteNotice
      guidanceHref={guidanceHref}
      onNavigate={() => {
        setOpen(false)
        onNavigate?.(guidanceHref)
      }}
    />
  )

  const Root = specimen ? 'div' : 'section'
  return (
    <Root
      className={`mds-banner mds-banner--${side} mds-banner--${how}${open && how === 'expand' ? ' is-open' : ''}`}
      aria-label={specimen ? undefined : isOfficial ? 'U.S. government websites' : 'About this site'}
      {...(prefs.bannerTone === 'dark' ? { 'data-theme': 'dark', 'data-mission': prefs.mission } : {})}
    >
      {how === 'dialog' ? (
        <>
          <button type="button" className="mds-banner__button" onClick={() => setOpen(true)} aria-haspopup="dialog">
            {flag}
            <span>{text}</span>
            {previewTag}
            <Icon name="info" size={16} className="mds-banner__info" />
          </button>
          <Dialog
            open={open}
            onClose={() => setOpen(false)}
            title={isOfficial ? 'U.S. Government Websites' : 'About this site'}
            className="mds-dialog--notice"
          >
            {explanation}
          </Dialog>
        </>
      ) : (
        <>
          <p className="mds-banner__line">
            {flag}
            <span>{text}</span>
            {previewTag}
            <button
              type="button"
              className="mds-banner__toggle"
              aria-expanded={open}
              aria-controls={panelId}
              onClick={() => setOpen((state) => !state)}
            >
              Here’s how you know
              <Icon name="chevron" size={12} className="mds-banner__chevron" />
            </button>
          </p>
          <div id={panelId} className="mds-banner__panel" hidden={!open}>
            <div className="mds-banner__panel-inner">
              {isOfficial ? (
                <>
                  <OfficialNotice />
                  {preview && previewNote}
                </>
              ) : (
                <SiteNotice guidanceHref={guidanceHref} onNavigate={() => onNavigate?.(guidanceHref)} />
              )}
            </div>
          </div>
        </>
      )}
    </Root>
  )
}
